import {
  createColumnVisibilityStore,
  type ColumnDefinition,
} from "./createColumnVisibilityStore";

/**
 * The payment table's columns, in render order.
 *
 * There is no Status column: the payment's status badge sits under its number instead, where the
 * thing it describes is named. It was a column of its own for one badge, and splitting an
 * identifier from its state across the table made a row harder to read, not easier.
 *
 * Labels match the table headers exactly — the menu is a list of what is on screen, so a different
 * wording there reads as a different column.
 *
 * Three are locked. `index` carries the select-all checkbox in the same cell as the row number
 * (see the header comment in `PaymentListTable`), so hiding it would take the bulk cancellation
 * away rather than free up width. `paymentNumber` is the only thing that says which payment a row
 * is, and it is the link to the detail screen. `actions` is where Verify / Reject / Refund live.
 */
export const PAYMENT_COLUMNS = [
  { id: "index", label: "Selection / STT", locked: true },
  { id: "paymentNumber", label: "Payment Number", locked: true },
  // T-PayGate's own code for the QR bill — what the gateway's records quote. Empty ("—") for any
  // payment the gateway did not issue. Shown by default, so no version bump: the store keeps the
  // hidden set, and a new id is absent from it.
  { id: "billCode", label: "Bill Code" },
  { id: "order", label: "Order" },
  { id: "user", label: "User" },
  { id: "tenant", label: "Tenant" },
  { id: "proof", label: "Proof" },
  { id: "amount", label: "Amount" },
  { id: "method", label: "Method" },
  // Verified sits before Created on purpose: this list is worked from the verification queue
  // outwards, so "has someone approved it, and who?" is the question being asked of a row, and
  // the answer belongs next to Status rather than after the creation date.
  { id: "verifiedAt", label: "Verified" },
  { id: "createdAt", label: "Created" },
  // Hidden until an operator asks for it: only a minority of payments carry a note, and prose is
  // the widest thing this table can hold. The Note column in the menu is how it is turned on.
  { id: "note", label: "Note", hiddenByDefault: true },
  { id: "actions", label: "Actions", locked: true },
] as const satisfies readonly ColumnDefinition[];

export type PaymentColumnId = (typeof PAYMENT_COLUMNS)[number]["id"];

export const usePaymentColumnsStore =
  createColumnVisibilityStore<PaymentColumnId>({
    name: "admin.payments.columns",
    columns: PAYMENT_COLUMNS,

    // v2 introduces `note`. The store remembers what is HIDDEN, so a column added later is absent
    // from every stored preference and would appear for everyone who has ever opened this screen —
    // exactly what `hiddenByDefault` is there to prevent. Bumping the version drops the stored set
    // so the defaults apply again; the cost is one reset of each operator's column choices here.
    version: 2,
  });
